import styled from 'styled-components';
import ProductResolutionImage from 'ggApp/shared/components/resolutionImage/ProductResolutionImage';
import ShimmerEffect from 'ggApp/shared/components/shimmerEffect';
import omitProps from 'ggApp/utils/omitProps';
import { elevation, elevationTransition } from 'ggApp/utils/mixins';

import zoomIcon from './zoom-icon.svg';

const ShimmerImage = ShimmerEffect(ProductResolutionImage);

export const Root = styled.div`
    display: flex;
    visibility: ${({ isOpen }) => (!isOpen ? 'visible' : 'hidden')};
    opacity: ${({ isOpen }) => (!isOpen ? 1 : 0)};
    transition-property: opacity, visibility;
    transition-duration: 0ms;
    transition-delay: ${({ isOpen }) => !isOpen && '200ms'};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile.max}px) {
        height: 275px;
        display: flex;
        flex-direction: column-reverse;
        justify-content: center;
        align-items: center;
    }
`;
// TODO make points styles
export const ThumbList = styled.ul`
    margin-right: 16px;
    max-width: 98px;
    ${({ theme }) => theme.superQuery.all.and.maxWidth.mobileMax.css`
        margin-right: 0;
        max-width: unset;
    `};
`;

export const ThumbImg = styled(ShimmerImage)`
    object-fit: contain;
    height: 70px;
    width: 70px;
`;

export const ThumbWrap = styled.li`
    border: ${({ thumbnailStyle }) => thumbnailStyle === 'thumbnailV2' ? 'none' : 'solid 2px rgba(0, 0, 0, 0)'};
    border-radius: ${({ thumbnailStyle }) => thumbnailStyle === 'thumbnailV2' ?'10px' : '8px'};
    &:hover {
        border-color: ${({ thumbnailStyle }) => thumbnailStyle === 'thumbnailV2' ? 'transparent' : '#e1e1e1'};
        background-color: ${({ thumbnailStyle }) => thumbnailStyle === 'thumbnailV2' ? '#fff' : 'transparent'};
        box-shadow: ${({ thumbnailStyle }) => thumbnailStyle === 'thumbnailV2' && '0px 7px 15px rgba(0, 0, 0, 0.13), 0px 0px 3px rgba(0, 0, 0, 0.05)'};
    }
    border-color: ${({ active, thumbnailStyle }) => active && thumbnailStyle !== 'thumbnailV2' ? '#e1e1e1' : 'transparent'};
    background-color: ${({ active, thumbnailStyle }) => thumbnailStyle === 'thumbnailV2' && active && '#fff'};
    box-shadow: ${({ active, thumbnailStyle }) => thumbnailStyle === 'thumbnailV2' && active && '0px 7px 15px rgba(0, 0, 0, 0.13), 0px 0px 3px rgba(0, 0, 0, 0.05)'};
    padding: 12px;
    ${({ theme }) => theme.superQuery.all.and.maxWidth.mobileMax.css`
        &:hover {
            border-color: rgba(0, 0, 0, 0);
        }
        padding: 0;
        display: inline-block;
        margin: 0 4px;
        height: 8px;
        width: 8px;
        border-radius: 8px;
        border: 2px solid #989898;
        background: #989898;
        &.active {
            background: none;
            border-color: #e1e1e1;
        }
        ${ThumbImg} {
            display: none;
        }
    `};
`;

export const ZoomButton = styled.button`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-24px, -24px);
    border-radius: 50%;
    height: 48px;
    width: 48px;
    background-color: #ffffff;
    background-image: url(${zoomIcon});
    background-repeat: no-repeat;
    background-size: 18px 18px;
    background-position: 50% 50%;
    cursor: pointer;
    opacity: 0;
    visibility: hidden;
    transition-property: visibility, opacity;
    transition-duration: 300ms;
    ${elevation(0)}
    ${elevationTransition()};
    @media not all and (any-pointer: coarse) {
        &:hover {
            ${elevation(3)};
        }
    }
    &:active {
        ${elevation(1)};
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile.max}px) {
        display: none;
    }
`;

export const Slider = styled.div`
    height: 100%;
    width: 100%;
    position: relative;
    @media not all and (any-pointer: coarse) {
        &:hover {
            ${ZoomButton} {
                visibility: visible;
                opacity: 1;
            }
        }
    }
    &::-webkit-scrollbar {
        display: none;
        height: 0 !important;
        width: 0 !important;
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile.max}px) {
        scroll-snap-type: x mandatory; /* Chrome Canary */
        scroll-snap-type-x: mandatory; /* Firefox */
        scroll-snap-destination: 0 50%; /* older (Firefox/IE) */
        scroll-snap-points-y: repeat(100%);
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        overflow-x: scroll;
        white-space: nowrap;
        scroll-padding: 0;
        ${ZoomButton} {
            visibility: hidden;
            opacity: 0;
        }
        :before {
            content: '';
            display: inline-block;
            vertical-align: middle;
            height: 100%;
        }
        height: 279px;
        padding-bottom: 20px;
    }
`;

export const SlideImage = styled(omitProps('visible')(ShimmerImage))`
    height: 100%;
    width: 100%;
    max-height: 100%;
    object-fit: contain;
    align-self: center;
    visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
    opacity: ${({ visible }) => (visible ? 1 : 0)};
    transition-property: opacity, visibility;
    transition-duration: 300ms;
    position: absolute;
    top: 0;
    left: 0;
    /* stylelint-disable */
    user-drag: none;
    /* stylelint-enable */
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile.max}px) {
        scroll-snap-align: start;
        scroll-snap-coordinate: 0 50%; /* older (Firefox/IE) */
        -webkit-scroll-snap-coordinate: 0 50%; /* older (Safari) */
        scroll-snap-stop: always;
        position: relative;
        max-width: 100%;
        min-height: 100%;
        height: 100%;
        width: 100%;
        visibility: visible;
        opacity: 1;
        vertical-align: middle;
        display: inline-block;
        /* Shimmer fixes */
        & > div:nth-child(2) {
            left: 0;
        }
        & > div:first-child img {
            padding: 0 calc(50vw - 120px);
        }
    }
`;

export const SliderWrap = styled.div`
    overflow: hidden;
    height: 500px;
    width: 100%;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile.max}px) {
        height: 100%;
    }
`;
